// src/routes/guards.js
//
// Las guardas ya no preguntan "¿qué rol tienes?" sino "¿tienes permiso sobre este
// módulo?". Es lo que permite crear un rol desde el panel y que entre a las pantallas
// que le marcaron, sin tocar este archivo ni App.js.
//
// Esto ordena la navegación; NO es la barrera de seguridad. La barrera son los
// `authorizeModulo` del backend, que verifican el mismo mapa de permisos.
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import useAuthContext from '../hooks/useAuthContext';
import { Spin } from 'antd';
import { can, puedeAprobar } from '../config/rbac';

// Componente de loading reutilizable
const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <Spin size="large" />
      <p className="mt-4 text-gray-600">Verificando permisos...</p>
    </div>
  </div>
);

// Solo invitados (sin sesión)
export const GuestRoute = ({ redirectTo = '/perfil' }) => {
  const { isSignedIn, loading } = useAuthContext();

  if (loading) {
    return <LoadingScreen />;
  }


  return !isSignedIn ? <Outlet /> : <Navigate to={redirectTo} replace />;
};

/**
 * Guarda por permiso de módulo.
 *
 * `modulos` acepta uno o varios: la pantalla se abre si se tiene el permiso sobre
 * CUALQUIERA de ellos (el historial de ciclos, por ejemplo, lo abren talleres y
 * reportería).
 *
 * @param {string|string[]} modulos  claves del catálogo (ver config/rbac.js)
 * @param {'ver'|'editar'}   accion
 */
export const RequireModulo = ({ modulos, accion = 'ver', redirectTo = '/unauthorized' }) => {
  const { isSignedIn, user, loading } = useAuthContext();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Los permisos llegan en una segunda petición. Sin esta espera, la primera pintada
  // vería el mapa vacío y mandaría a /unauthorized a quien sí tiene acceso.
  //
  // El `!user` importa: con la sesión ya iniciada pero el usuario todavía sin
  // hidratar, `permisosCargados` es `undefined` —no `false`—, así que la espera no
  // se activaba y se evaluaba `can(null, …)`. Resultado: un 403 espurio en la
  // primera pintada, visible sobre todo tras un recarga en caliente.
  if (!user || user.permisosCargados === false) {
    return <LoadingScreen />;
  }

  const lista = Array.isArray(modulos) ? modulos : [modulos];
  const tienePermiso = lista.some(modulo => can(user, modulo, accion));

  return tienePermiso ? <Outlet /> : <Navigate to={redirectTo} replace />;
};

/**
 * Guarda para las pantallas que RESUELVEN una cola de revisión.
 *
 * Es `editar`, menos quien necesita que le aprueben: nadie se aprueba a sí mismo. El
 * menú esconde la entrada con la misma regla y el backend la hace cumplir.
 */
export const RequireAprobador = ({ modulo, redirectTo = '/unauthorized' }) => {
  const { isSignedIn, user, loading } = useAuthContext();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Mismo motivo que en RequireModulo: sin el usuario hidratado, `permisosCargados`
  // es `undefined` y se evaluaría `puedeAprobar(null, …)`.
  if (!user || user.permisosCargados === false) {
    return <LoadingScreen />;
  }

  return puedeAprobar(user, modulo) ? <Outlet /> : <Navigate to={redirectTo} replace />;
};

/**
 * Cualquier persona con sesión, sin importar su rol.
 *
 * Es para las pantallas del sitio público que requieren cuenta (perfil, mis talleres,
 * familiares). Antes esto era una lista de roles en App.js, y por eso un rol nuevo
 * —`funcionario`, por ejemplo— no podía ni ver su propio perfil.
 */
export const RequireSesion = ({ redirectTo = '/login' }) => {
  const { isSignedIn, loading } = useAuthContext();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  return isSignedIn ? <Outlet /> : <Navigate to={redirectTo} replace state={{ from: location }} />;
};
